import { forwardRef, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Alert, Button, DialogActions, DialogContent, Stack } from '@customink/pigment-react';
import { useNotifications } from '~/contexts/NotificationContext';
import { isAxiosErrorWithHttpStatus } from '~/adapters/axios/isAxiosError';
import {
    PostalAddress,
    PostalAddressVerificationError,
    ValidatedField,
} from '~/adapters/services/accounts/postalAddresses/types';
import Spinner from '~/components/Spinner';
import { ResponsiveDialog } from '~/components/DialogPigment/ResponsiveDialog';
import { AddressFormState, DialogProps } from '../types';
import { FirstName } from './controlledInputs/FirstName';
import { LastName } from './controlledInputs/LastName';
import { AddressCountry } from './controlledInputs/AddressCountry';
import { StreetAddress } from './controlledInputs/StreetAddress';
import { AddressDescription } from './controlledInputs/AddressDescription';
import { AddressLocality } from './controlledInputs/AddressLocality';
import { AddressRegion } from './controlledInputs/AddressRegion';
import { PostalCode } from './controlledInputs/PostalCode';
import { Telephone } from './controlledInputs/Telephone';
import { AddressType } from './controlledInputs/AddressType';
import { IsDefault } from './controlledInputs/IsDefault';

const RootFormError = forwardRef<HTMLDivElement, { error?: string }>(({ error }, ref) => (
    <div ref={ref}>
        {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
                {error}
            </Alert>
        )}
    </div>
));

interface AddressDialogProps extends DialogProps {
    title: string;
    onSubmit: (param: AddressFormState) => Promise<PostalAddress>;
    initialAddressState: AddressFormState;
    isLoading: boolean;
}

export function AddressDialog({ title, open, onClose, initialAddressState, onSubmit, isLoading }: AddressDialogProps) {
    const { pushApiErrorNotification } = useNotifications();

    const formErrorRef = useRef<HTMLDivElement | null>(null);

    const {
        control,
        handleSubmit,
        formState: { errors },
        setValue,
        setError,
    } = useForm<AddressFormState>({
        defaultValues: initialAddressState,
    });

    const handleVerificationError = useCallback(
        ({ fieldErrors }: PostalAddressVerificationError) => {
            const fieldErrorEntries = Object.entries(fieldErrors) as [ValidatedField, string][];
            // display a generic error from EasyPost that does not point to any particular field
            if (!fieldErrorEntries.length) {
                setError('root', { message: 'Address was not found.' });
                formErrorRef.current?.scrollIntoView({ behavior: 'smooth' });
                return;
            }
            // display specific errors for each field marked by EasyPost
            fieldErrorEntries.forEach(([fieldName, errorMessage]) => {
                setError(fieldName, { message: errorMessage });
            });
        },
        [setError],
    );

    const handleGenericApiError = useCallback(() => {
        pushApiErrorNotification('An error occurred while saving address.');
        onClose();
    }, []);

    const validateAndSubmit = useCallback(
        handleSubmit(async (formState: AddressFormState) => {
            try {
                await onSubmit(formState);
                onClose();
            } catch (err) {
                if (isAxiosErrorWithHttpStatus(err, 422) && err.response) {
                    handleVerificationError(err.response.data as PostalAddressVerificationError);
                    return;
                }
                handleGenericApiError();
            }
        }),
        [onSubmit, handleSubmit, handleVerificationError, handleGenericApiError],
    );

    return (
        <ResponsiveDialog title={title} open={open} onClose={onClose} fullWidth>
            <DialogContent>
                <RootFormError error={errors.root?.message} ref={formErrorRef} />
                <Stack direction="row" gap={2}>
                    <FirstName control={control} fieldErrors={errors} />
                    <LastName control={control} fieldErrors={errors} />
                </Stack>
                <AddressCountry control={control} setValue={setValue} />
                <StreetAddress control={control} fieldErrors={errors} />
                <AddressDescription control={control} fieldErrors={errors} />
                <AddressLocality control={control} fieldErrors={errors} />
                <AddressRegion control={control} fieldErrors={errors} />
                <PostalCode control={control} fieldErrors={errors} />
                <Telephone control={control} fieldErrors={errors} setValue={setValue} />
                <AddressType control={control} />
                <IsDefault control={control} />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="secondary">
                    Cancel
                </Button>
                <Button variant="primary" onClick={validateAndSubmit} disabled={isLoading}>
                    {isLoading ? <Spinner /> : 'Save'}
                </Button>
            </DialogActions>
        </ResponsiveDialog>
    );
}
